import React, { useEffect, useMemo, useRef, useState } from 'react';
import EditorString from '../EditorString/EditorString';
import { EditorStringModel } from '../../Types/EditorString';
import { translate } from '../../Services/translation';
import QuickOrderDesktopTable from './QuickOrderDesktopTable';
import QuickOrderMobileTable from './QuickOrderMobileTable';
import { useDispatch } from 'react-redux';
import {
  QuickOrderCsvProduct,
  getProducts,
  QuickOrderProduct,
  getTotalPrice,
  QuickOrderActions,
} from '../../Reducers/QuickOrder.reducer';
import {
  receive as receiveCart,
  triggerAddToCartAnimation,
} from '../../Actions/Cart.action';
import { useTypedSelector } from '../../Hooks/useTypedSelector';
import { addMultiple } from '../../Services/QuickOrder.service';
import clsx from 'clsx';
import BuyButton from '../BuyButton/BuyButton';
import { triggerToast } from '../../Services/Toast.service';
import { PRICE_TOGGLE_BEFORE_RELOAD_EVENT } from '../../constants';

interface QuickOrder {
  title: string;
  description: EditorStringModel;
  disableBuyOption: boolean;
}

const mapToExtractProduct = (product: QuickOrderProduct) => {
  const result = {
    ArticleNumber: product.id,
    Name: product.name,
    Collection: product.itemCollection,
    Quantity: product.totalAmount?.total,
    Note: product?.note ?? '',
    DisableBuyOption: product?.disableBuyOption ?? '',
  };

  return result;
};

const QuickOrder = (props: QuickOrder) => {
  const { result, updatePending, errors } = useTypedSelector(
    (state) => state.QuickOrderReducer
  );

  const priceToggleEventRef = useRef(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showLoadingSpinner, setShowLoadingSpinner] =
    useState<boolean>(false);
  const [showCheckmark, setShowCheckmark] = useState<boolean>(false);
  const [csvData, setCsvData] = useState<QuickOrderCsvProduct[]>();

  const dispatch = useDispatch();

  const opacityTransitionClasses = 'transition duration-[1500ms]';
  const stateIconClasses = clsx(
    'left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2',
    opacityTransitionClasses
  );

  const exportAsCvs = () => {
    const csvRows: any[] = [];
    const headers = Object.keys(mapToExtractProduct(result[0]!!));
    csvRows.push(headers.join(';'));

    result.map((res) => {
      const mappedProduct = mapToExtractProduct(res);
      const values = Object.values(mappedProduct).join(';');
      csvRows.push(values);
    });

    download(csvRows.join('\n'));
  };

  const download = (data) => {
    const blob = new Blob(['\ufeff', data], {
      type: 'text/csv;charset=ISO-8859-1',
    });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');

    a.setAttribute('href', url);
    a.setAttribute(
      'download',
      translate('quick-order') +
        '_' +
        new Date().toISOString().split('T')[0] +
        '.csv'
    );

    a.click();
  };

  useEffect(() => {
    if (csvData) {
      dispatch(getProducts(csvData));
    }
  }, [csvData]);

  useEffect(() => {
    const sessionData = sessionStorage.getItem('quickOrderCsvRows');

    if (sessionData) {
      try {
        const parsedData = JSON.parse(sessionData);

        if (parsedData && parsedData.length > 0) setCsvData(parsedData);
      } catch {}
    }

    window.addEventListener(PRICE_TOGGLE_BEFORE_RELOAD_EVENT, () => {
      priceToggleEventRef.current = true;
    });

    window.addEventListener('unload', () => {
      if (!priceToggleEventRef.current)
        sessionStorage.removeItem('quickOrderCsvRows');
    });
  }, []);

  useEffect(() => {
    let quickOrderCsvRows = [] as QuickOrderCsvProduct[];

    if (result) {
      quickOrderCsvRows = result.map((res) => ({
        ArticleNumber: res.id!,
        Quantity: String(res.totalAmount.total),
        Note: res.note!,
      }));
    }

    sessionStorage.setItem(
      'quickOrderCsvRows',
      JSON.stringify(quickOrderCsvRows)
    );
  }, [result]);

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    const csvData: QuickOrderCsvProduct[] = [];
    const reader = new FileReader();
    reader.onload = () => {
      if (reader.result) {
        const result = reader.result.replace(/(\r||\r)/gm, '');
        const rows = result.split('\n');
        const headers = rows[0].split(';');
        const columnIndices = {};

        for (const i in headers) {
          columnIndices[headers[i.replace(/\s+|\r\n/g, '')]] = i.replace(
            /\s+|\r\n/g,
            ''
          );
        }

        rows.slice(1).forEach((row) => {
          const columns = row.split(';');
          csvData.push({
            ArticleNumber: columns[columnIndices['ArticleNumber']],
            Quantity: columns[columnIndices['Quantity']],
            Note: columns[columnIndices['Note']],
          });
        });

        setCsvData(csvData);
      }
    };

    reader.readAsText(file, 'ISO-8859-1');
  };

  const itemsTotalPrice = useMemo(
    () => result.reduce((prev, item) => prev + item.totalAmount.total, 0),
    [result]
  );

  useEffect(() => {
    const timeOutId = setTimeout(() => {
      if (!updatePending) dispatch(getTotalPrice(result));
    }, 500);

    return () => {
      clearTimeout(timeOutId);
    };
  }, [itemsTotalPrice]);

  useEffect(() => {
    errors?.length > 0 &&
      errors?.map((error) => {
        if (error.replacement) {
          triggerToast({
            title: error.title,
            text: `${error.message} ${error.replacement}`,
            type: 'error',
            autoClose: false,
          });
        } else {
          triggerToast({
            title: error.title,
            text: error.message,
            type: 'error',
            autoClose: false,
          });
        }
      });
  }, [errors, result, dispatch]);

  return (
    <div className="container container--narrow pt-6 lg:pt-10">
      <div className="mb-10 max-w-[568px]">
        {props.title && <h1>{props.title}</h1>}
        {props.description?.value && (
          <EditorString value={props.description.value} />
        )}
      </div>
      <div className="my-4 flex items-center justify-end">
        <label className="cursor-pointer rounded-md border border-solid border-gray-700 px-4 py-3 text-sm font-normal uppercase md-down:flex-1">
          {translate('quick-order.import')}
          <input
            name="file"
            type="file"
            accept=".csv"
            className="hidden"
            onChange={handleFileChange}
          />
        </label>
      </div>
      <div className="flex flex-1">
        <QuickOrderMobileTable />
        <QuickOrderDesktopTable />
      </div>
      <div className="my-4 flex items-center justify-end gap-4 md-down:flex-col">
        <button
          className="rounded-md border border-solid border-gray-700 px-4 py-3 text-sm font-normal uppercase md-down:w-full md-down:flex-1"
          onClick={(e) => exportAsCvs()}
        >
          {translate('quick-order.export')}
        </button>
        {!props.disableBuyOption && (
          <BuyButton
            disabled={result.length == 0 || isLoading || showCheckmark}
            label={translate('quick-order.cart-add')}
            className={clsx(
              'rounded-md px-4 py-3 text-center text-sm font-normal uppercase text-white md:!w-auto md-down:w-full md-down:flex-1'
            )}
            onClick={async () => {
              const cart = await addMultiple(result);
              dispatch(receiveCart(cart));
              dispatch(triggerAddToCartAnimation());
              dispatch(QuickOrderActions.clearResultRow());
            }}
          />
        )}
      </div>
    </div>
  );
};

export default QuickOrder;
